import React, { type Ref } from 'react'
import clsx from 'clsx'
import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'
import type { IconTypes } from '../utils/Icon'
import { type ButtonStyleProps, rankColorClasses, sizeClasses } from './ButtonStyles'

type ButtonBaseProps = ButtonStyleProps & {
  trailingIcon?: IconTypes
  leadingIcon?: IconTypes
  type?: 'submit' | 'button' | 'reset'
  onClick?: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  dataCompId?: string
  role?: string
  className?: string
  linkBackground?: boolean
  ref?: Ref<HTMLButtonElement>
}

// a11y: buttons must have either a text node (children) or an aria-label
export type ButtonProps = ButtonBaseProps &
  ({ children: React.ReactNode; ariaLabel?: string } | { children?: never; ariaLabel: string })

const Button = ({
  size = 'md',
  rank = 'primary',
  color = 'primary',
  linkBackground = true,
  leadingIcon,
  trailingIcon,
  type = 'button',
  children,
  onClick,
  ariaLabel,
  disabled,
  dataCompId,
  className = '',
  ref,
  ...props
}: ButtonProps) => {
  return (
    <button
      disabled={disabled}
      aria-disabled={disabled}
      type={type}
      onClick={onClick}
      aria-label={ariaLabel}
      ref={ref}
      {...props}
      className={cn(
        'tw:rounded-lg tw:transition',
        'tw:flex tw:items-center tw:gap-2',
        'tw:focus-visible:border-primary-500 tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
        'tw:data-[state=open]:border-primary-500 tw:data-[state=open]:ring-4 tw:data-[state=open]:ring-primary-300',
        sizeClasses(size),
        rankColorClasses(rank, color, linkBackground),
        className
      )}
      {...(dataCompId && { 'data-compid': `${dataCompId}-button` })}
    >
      {leadingIcon && (
        <Icon dataTestId="leading-icon" type={leadingIcon} className="tw:h-4.5 tw:w-4.5 tw:mt-0.5" />
      )}
      {children && (
        <span
          className={clsx(
            'tw:gap-2 tw:whitespace-nowrap tw:flex tw:flex-nowrap tw:items-center tw:justify-center',
            (leadingIcon ?? trailingIcon) ? 'tw:relative tw:top-px' : ''
          )}
        >
          {children}
        </span>
      )}
      {trailingIcon && (
        <Icon dataTestId="trailing-icon" type={trailingIcon} className="tw:h-4.5 tw:w-4.5 tw:mt-0.5" />
      )}
    </button>
  )
}

export default Button
